import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { PeriodTypeEnum } from '@schooly/api';
import {
  DeleteIcon,
  EditIcon,
  SimpleCard,
  TagSimple,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { TimetableStructurePeriodForm } from '../../../../../context/timetableStructure/WithTimetableStructure';
import {
  getDurationFromMinutes,
  getDurationLabel,
  getMinutesDuration,
  getPeriodTypePalette,
} from '../../utils';
import { TimetableStructurePeriodAvatar } from './TimetableStructurePeriodAvatar';

interface TimetableStructurePeriodRowProps {
  period: TimetableStructurePeriodForm;
  onDelete: () => void;
  onEdit: () => void;
  errorMessage?: string;
}

export const TimetableStructurePeriodRow: FC<TimetableStructurePeriodRowProps> = ({
  period,
  onDelete,
  onEdit,
  errorMessage,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const palette = getPeriodTypePalette(period.type, theme);
  const registerPalette = getPeriodTypePalette(PeriodTypeEnum.Registration, theme);

  const hasError = !!errorMessage;

  const durationLabel = useMemo(() => {
    const durationInMinutes = getMinutesDuration(period.start_time, period.end_time);
    const duration = getDurationFromMinutes(durationInMinutes);

    return getDurationLabel(duration, {
      hours: formatMessage({ id: 'school-timetableStructure-Hours' }),
      minutes: formatMessage({ id: 'school-timetableStructure-Minutes' }),
    }).toUpperCase();
  }, [period.start_time, period.end_time, formatMessage]);

  return (
    <Stack gap={0.25} data-scope>
      <Stack direction="row" gap={2}>
        <SimpleCard
          withBorder
          sx={{
            borderRadius: (theme) => theme.spacing(1),
            borderColor: hasError ? 'error.main' : undefined,
            flex: 1,
            '& .editIcon': { visibility: 'hidden' },
            '&:hover': {
              '.editIcon': {
                visibility: 'visible',
              },
              '.MuiCardActionArea-focusHighlight': {
                background: 'transparent',
              },
              '.MuiCardActionArea-root': {
                background: (theme) => theme.palette.common.lightBg,
              },
            },
          }}
          onItemClick={onEdit}
          avatar={
            <TimetableStructurePeriodAvatar
              type={period.type}
              registerSession={period.register_session}
              sx={{ mr: 1 }}
            />
          }
          actions={
            <Stack alignItems="flex-end" height="100%" justifyContent="center">
              <IconButton className="editIcon" sx={{ p: 0 }}>
                <EditIcon />
              </IconButton>
            </Stack>
          }
          actionAreaProps={{ sx: { py: 1.5, px: 2 } }}
        >
          <Stack flex={1} gap={2} direction="row" alignItems="center" overflow="hidden">
            <Stack minWidth={90}>
              <Typography variant="h3">
                {period.start_time} - {period.end_time}
              </Typography>
              <Typography color="text.secondary">{durationLabel}</Typography>
            </Stack>
            <Stack alignItems="flex-start" overflow="hidden">
              <TypographyWithOverflowHint variant="h3" maxWidth="100%">
                {period.name}
              </TypographyWithOverflowHint>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {period.register_session != null && period.type === PeriodTypeEnum.Lesson && (
                  <TagSimple sx={{ backgroundColor: palette.secondary }}>
                    <Typography
                      variant="h4"
                      sx={(theme) => ({ color: palette.primary, lineHeight: theme.spacing(2.5) })}
                    >
                      {formatMessage({ id: 'school-timetableStructure-periodType-Lesson' })}
                    </Typography>
                  </TagSimple>
                )}
                {period.register_session != null ? (
                  <TagSimple sx={{ backgroundColor: registerPalette.secondary }}>
                    <Typography
                      variant="h4"
                      sx={(theme) => ({
                        color: registerPalette.primary,
                        lineHeight: theme.spacing(2.5),
                      })}
                    >
                      {formatMessage({
                        id: `school-timetableStructure-registerSession-${period.register_session}`,
                      })}
                    </Typography>
                  </TagSimple>
                ) : (
                  <TagSimple sx={{ backgroundColor: palette.secondary }}>
                    <Typography
                      variant="h4"
                      sx={(theme) => ({ color: palette.primary, lineHeight: theme.spacing(2.5) })}
                    >
                      {formatMessage({ id: `school-timetableStructure-periodType-${period.type}` })}
                    </Typography>
                  </TagSimple>
                )}
              </Stack>
            </Stack>
          </Stack>
        </SimpleCard>
        <IconButton onClick={onDelete} inverse>
          <DeleteIcon />
        </IconButton>
      </Stack>
      {hasError && (
        <Typography variant="caption" color="error.main">
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
};
